/**
 * TransactionLimitsPage — `/settings/transaction-limits`.
 *
 * Per-tier daily / single-transaction limits. Backed by the
 * generic settings-collection store on service-audit
 * (`GET / PUT /v1/settings-collections/transaction_limits`).
 */
import { type JSX, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';

import {
  getSettingsCollection,
  putSettingsCollection,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';
import { formatNaira } from '../mail/shared/MailUI';

interface LimitRow {
  readonly tier: string;
  readonly description: string;
  readonly singleMinor: number;
  readonly dailyMinor: number;
  readonly walletCapMinor: number;
}

const COLLECTION_KEY = 'transaction_limits' as const;

export function TransactionLimitsPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['settings-collection', COLLECTION_KEY],
    queryFn: () => getSettingsCollection<LimitRow>(COLLECTION_KEY),
    retry: false,
    staleTime: 30_000,
  });

  const [rows, setRows] = useState<readonly LimitRow[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (query.data && !dirty) {
      setRows(query.data.items);
    }
  }, [query.data, dirty]);

  const saveMut = useMutation({
    mutationFn: (next: readonly LimitRow[]) =>
      putSettingsCollection<LimitRow>(COLLECTION_KEY, next),
    onSuccess: async () => {
      setDirty(false);
      await qc.invalidateQueries({ queryKey: ['settings-collection', COLLECTION_KEY] });
    },
  });

  function update(idx: number, patch: Partial<LimitRow>): void {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setRows(next);
    setDirty(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Transaction limits"
        description="Set financial transaction limits by KYC level and franchise tier."
      />

      {query.isPending ? (
        <p className="text-sm text-muted">Loading limits…</p>
      ) : query.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Could not load limits: {(query.error as Error).message}
        </div>
      ) : null}

      {saveMut.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Save failed: {(saveMut.error as Error).message}
        </div>
      ) : null}

      <div className="bg-white border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-border">
            <tr>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Tier</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Description</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Single (₦)</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Daily (₦)</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Wallet cap (₦)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, idx) => (
              <tr key={r.tier} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs font-mono text-gray-900">{r.tier}</td>
                <td className="px-4 py-2 text-xs text-muted">{r.description}</td>
                <td className="px-4 py-1.5">
                  <input
                    type="number"
                    value={Math.round(r.singleMinor / 100)}
                    aria-label={`Single ${r.tier}`}
                    onChange={(e) => update(idx, { singleMinor: Number(e.target.value) * 100 })}
                    className="w-28 px-2 py-1 border border-border rounded text-xs text-right"
                  />
                  <p className="text-[10px] text-muted text-right mt-0.5">{formatNaira(r.singleMinor)}</p>
                </td>
                <td className="px-4 py-1.5">
                  <input
                    type="number"
                    value={Math.round(r.dailyMinor / 100)}
                    aria-label={`Daily ${r.tier}`}
                    onChange={(e) => update(idx, { dailyMinor: Number(e.target.value) * 100 })}
                    className="w-28 px-2 py-1 border border-border rounded text-xs text-right"
                  />
                  <p className="text-[10px] text-muted text-right mt-0.5">{formatNaira(r.dailyMinor)}</p>
                </td>
                <td className="px-4 py-1.5">
                  <input
                    type="number"
                    value={Math.round(r.walletCapMinor / 100)}
                    aria-label={`Wallet cap ${r.tier}`}
                    onChange={(e) => update(idx, { walletCapMinor: Number(e.target.value) * 100 })}
                    className="w-28 px-2 py-1 border border-border rounded text-xs text-right"
                  />
                  <p className="text-[10px] text-muted text-right mt-0.5">{formatNaira(r.walletCapMinor)}</p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!dirty || saveMut.isPending}
          onClick={() => saveMut.mutate(rows)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" /> {saveMut.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
